import React, { useState, useEffect } from "react";
import { getGlobalLeaderboard } from "../../api/communityApi";

const LeaderboardTab = () => {
  const [period, setPeriod] = useState("all-time"); // Backend curr only does all-time based on PnL
  const [leaders, setLeaders] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const data = await getGlobalLeaderboard();
        setLeaders(data.leaderboard || []);
        setMyRank(data.my_rank);
      } catch (error) {
        console.error("Failed to fetch leaderboard", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  // Format growth 
  const formatGrowth = (pct) => {
    if (pct == null) return "0.00%";
    const sign = pct >= 0 ? "+" : "";
    return `${sign}${pct.toFixed(2)}%`;
  };

  const getLeader = (index) => {
    return leaders[index] || {
      rank: index + 1,
      display_name: "-",
      total_return_pct: 0,
      avatar_url: `https://ui-avatars.com/api/?name=Empty`
    };
  };

  return (
    <div className="space-y-10 animate-fadeIn">

      {/* HEADER */}
      <div>
        <h2 className="text-3xl font-bold mb-2">Global Leaderboard</h2>
        <p className="text-gray-400">
          Track the top-performing investors on FinAI.
        </p>
      </div>

      {/* FILTER BUTTONS (UI only for now) */}
      <div className="flex gap-3 mb-6">
        {["daily", "weekly", "monthly", "all-time"].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-4 py-2 rounded-lg text-sm capitalize transition ${
              period === p
                ? "bg-indigo-600 text-white"
                : "bg-gray-800 dark:bg-gray-700 text-gray-300 hover:bg-gray-700"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500">Loading Leaderboard...</p>
      ) : (
        <>
          {/* TOP 3 PODIUM */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">

            {/* 🥈 SECOND PLACE */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 border border-gray-300 dark:border-gray-700 shadow flex flex-col items-center">
              <p className="text-lg font-medium text-gray-500">#2</p>
              <img
                src={getLeader(1).avatar_url || `https://ui-avatars.com/api/?name=${getLeader(1).display_name}`}
                className="w-20 h-20 rounded-full border-2 border-gray-500 object-cover"
                alt=""
              />
              <h3 className="mt-3 text-xl font-semibold truncate w-full">{getLeader(1).display_name}</h3>
              <p className={`${getLeader(1).total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'} text-md font-medium`}>
                {formatGrowth(getLeader(1).total_return_pct)}
              </p>
            </div>

            {/* 🥇 FIRST PLACE */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 border border-yellow-500 shadow-lg scale-105 flex flex-col items-center">
              <p className="text-lg font-medium text-yellow-500">#1</p>
              <img
                src={getLeader(0).avatar_url || `https://ui-avatars.com/api/?name=${getLeader(0).display_name}`}
                className="w-24 h-24 rounded-full border-4 border-yellow-500 object-cover"
                alt=""
              />
              <h3 className="mt-3 text-2xl font-bold truncate w-full">{getLeader(0).display_name}</h3>
              <p className={`${getLeader(0).total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'} text-lg font-bold`}>
                {formatGrowth(getLeader(0).total_return_pct)}
              </p>
            </div>

            {/* 🥉 THIRD PLACE */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 border border-orange-500 shadow flex flex-col items-center">
              <p className="text-lg font-medium text-orange-400">#3</p>
              <img
                src={getLeader(2).avatar_url || `https://ui-avatars.com/api/?name=${getLeader(2).display_name}`}
                className="w-20 h-20 rounded-full border-2 border-orange-400 object-cover"
                alt=""
              />
              <h3 className="mt-3 text-xl font-semibold truncate w-full">{getLeader(2).display_name}</h3>
              <p className={`${getLeader(2).total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'} text-md font-medium`}>
                {formatGrowth(getLeader(2).total_return_pct)}
              </p>
            </div>
          </div>

          {/* FULL RANKING TABLE */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-xl border border-gray-300 dark:border-gray-700 shadow-lg mt-8">

            <h3 className="text-xl font-semibold mb-4">Complete Rankings</h3>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-gray-500 text-sm border-b border-gray-300 dark:border-gray-700">
                    <th className="pb-2">Rank</th>
                    <th>Name</th>
                    <th className="hidden sm:table-cell">Holdings</th>
                    <th>Total Return</th>
                  </tr>
                </thead>

                <tbody>
                  {leaders.map((u, i) => (
                    <tr
                      key={u.user_id}
                      className={`border-b border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition ${myRank?.user_id === u.user_id ? 'bg-indigo-900/40 dark:bg-indigo-900/40' : ''}`}
                    >
                      <td className="py-3 font-semibold">#{u.rank}</td>
                      <td className="flex items-center gap-3 py-3">
                        <img
                          src={u.avatar_url || `https://ui-avatars.com/api/?name=${u.display_name}`}
                          className="w-10 h-10 rounded-full object-cover"
                          alt=""
                        />
                        {u.display_name} {myRank?.user_id === u.user_id && "(You)"}
                      </td>
                      <td className="font-medium hidden sm:table-cell">
                        {u.num_holdings} positions
                      </td>
                      <td
                        className={`font-medium ${
                          u.total_return_pct >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {formatGrowth(u.total_return_pct)}
                      </td>
                    </tr>
                  ))}
                  
                  {leaders.length === 0 && (
                    <tr>
                      <td colSpan="4" className="py-6 text-center text-gray-500">
                        No users have connected their portfolios yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* YOUR RANK HIGHLIGHT */}
            {myRank && (
              <div className="mt-6 bg-indigo-900/40 border border-indigo-700 p-4 rounded-xl text-indigo-300 text-sm">
                ⭐ You are ranked <span className="font-bold">#{myRank.rank}</span> out of {leaders.length} users with {formatGrowth(myRank.total_return_pct)} returns.
              </div>
            )}
            {!myRank && leaders.length > 0 && (
              <div className="mt-6 bg-gray-800 border border-gray-700 p-4 rounded-xl text-gray-400 text-sm">
                Connect your brokerage account on the Portfolio page to join the leaderboard!
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LeaderboardTab;
